import React, { FC } from 'react'

import { useDropzone } from 'react-dropzone'
import styled, { keyframes } from 'styled-components'

import { Txt, Icon } from 'src/components/atoms'
import { CommonContentAssetDropArea, CommonContentItemBase } from 'src/components/molecules'

import {
  useCommonContentAsset,
  useCommonContentPageLoading,
  useGetCommonContentPages,
  useGetPromiseCommonPage,
  useToast,
} from 'src/hooks'

const flash = keyframes`
  from {
    opacity: 0.4;
  }
  to {
    opacity: 1;
  }
`

const Container = styled(CommonContentItemBase)`
  background-image: linear-gradient(
    -45deg,
    #eff1f4 25%,
    #fff 25%,
    #fff 50%,
    #eff1f4 50%,
    #eff1f4 75%,
    #fff 75%,
    #fff
  );
  background-size: 0.5rem 0.5rem;

  &:hover:not(:disabled) {
    opacity: 1;
    animation-duration: 1s;
    animation-name: ${flash};
  }
`

type Props = {
  commonContentAssetsCount: number
  commonContentPageId: number
  className?: string
}

export const CreateNewCommonContentFile: FC<Props> = ({
  commonContentAssetsCount,
  commonContentPageId,
  className,
}) => {
  const { createCommonContentAsset, uploadAssetFile } = useCommonContentAsset()
  const { refetch } = useGetCommonContentPages()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { loadCommonPage } = useGetPromiseCommonPage()
  const { showCommonContentPageLoading, hideCommonContentPageLoading } =
    useCommonContentPageLoading()

  const handleOnCreate = async () => {
    showCommonContentPageLoading()
    try {
      await createCommonContentAsset({
        commonContentPageId,
        pageOrder: commonContentAssetsCount + 1,
      })
      refetch()
      displayStandardTextToast('保存しました')
    } catch {
      displayErrorTextToast('作成に失敗しました、再度お試しください')
    } finally {
      hideCommonContentPageLoading()
    }
  }

  const onDrop = async (acceptedFiles: any) => {
    if ((acceptedFiles as FileList).length > 1) {
      displayErrorTextToast('単体のファイルのみアップロードが可能です')
      return
    }

    showCommonContentPageLoading()

    // まず新しいアセットを作成する
    try {
      await createCommonContentAsset({
        commonContentPageId,
        pageOrder: commonContentAssetsCount + 1,
      })
      refetch()
    } catch {
      displayErrorTextToast('作成に失敗しました、再度お試しください')
      hideCommonContentPageLoading()
      return
    }

    // アセットを作成した共通コンテンツページの最新の状態を取得
    const page = await loadCommonPage(commonContentPageId)
    // 上で作成したアセット（priorityが最後のもの）を取得
    const lastAsset = [...page.assets].sort((a, b) => b.priority - a.priority)[0]

    try {
      await uploadAssetFile({ asset: lastAsset, file: acceptedFiles[0] })
      refetch()
      displayStandardTextToast('保存しました')
    } catch {
      displayErrorTextToast('アップロードに失敗しました')
    } finally {
      hideCommonContentPageLoading()
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // noClickを設定する事でclickによるファイル選択を無効化し、子要素の handleOnCreate のみを呼び出す
    noClick: true,
  })

  return (
    <div className={className}>
      <div {...getRootProps()}>
        <Container
          showShadow
          className="tw-text-standardText-secondary tw-centering tw-flex-col tw-cursor-pointer tw-relative"
          onClick={handleOnCreate}
        >
          <div className="tw-flex tw-items-center">
            <Icon size="--r0" path="plus/soft/line" />
            <Txt sizeType="s" className="tw-font-bold tw-ml-s-4">
              新規ファイル追加
            </Txt>
          </div>
          <Txt sizeType="s" className="tw-mt-s-1">
            クリックまたはファイルを直接ドラッグ＆ドロップ
          </Txt>

          {isDragActive && (
            <CommonContentAssetDropArea className="tw-absolute tw-w-full tw-h-full tw-top-0 tw-left-0 tw-z-50" />
          )}
        </Container>

        <input {...getInputProps()} type="file" hidden accept=".png,.jpg,.gif,.pdf,.mp4" />
      </div>
    </div>
  )
}
